/* Gourdeous Porches — marketing-site behavior (from design handoff).
 * Nav shrink, mobile menu, ZIP→email capture funnel, band signup,
 * and market-aware content swapping driven by window.GPMetro (gp_metro cookie).
 * v9 (2026-07-23): footer serve links route to the METRO HOMEPAGE — they set
 * the metro (GPMetro.set) and navigate to "/?metro=<key>"; the param is also
 * honored on load as a no-JS / late-load fallback.
 * v11 (2026-08-05, BOOKING-OPEN dev theme only): hero open flow — ZIP check →
 * in-area shows "booking is open" + Book your porch CTA ([data-gp-capture-open],
 * initHeroCaptureOpen); footer serve column appends "Book your porch" instead of
 * "Get first access". Pre-open handlers kept (they no-op without their hooks).
 * v12 (2026-08-06): scroll memory — Horizon scrolls an inner .page-wrapper, so
 * the browser's own back/forward scroll restoration can't restore position
 * ("See details" → back landed at the top). We remember the scroller position
 * per URL in sessionStorage and restore it on back/forward navigations.
 * v14 (2026-08-06): resume CTA — when a saved booking draft exists (gp-booking.js
 * writes localStorage "gp-booking-draft"), nav/menu "Book your porch" links become
 * "Resume your porch · $X" so the in-progress order is one tap away.
 * v18 (2026-08-12): the bag total also re-reads on "gp:cart" — the cart page can
 * change the total without a reload (ZIP zone fee, qty, remove), and load/pageshow
 * alone left the nav showing the old number.
 * v19 (2026-08-12): on /cart the draft is the ONLY source for the bag — the
 * /cart.js fallback is for marketing pages after a checkout bounce, and on the cart
 * page it could contradict the page itself. Late replies can no longer repaint.
 * v20 (2026-08-12): an out-of-area ZIP in the hero now offers the waitlist instead
 * of dead-ending — reuses postWaitlist, so misses are captured and segmented.
 */
(function () {
  "use strict";

  /* ---------- shared data ---------- */
  function marketData() {
    var el = document.getElementById("gp-market-data");
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }
  var DATA = null;
  function data() { return DATA || (DATA = marketData()); }

  /* Horizon scrolls an inner .page-wrapper (html/body are overflow:hidden). */
  function scroller() {
    var pw = document.querySelector(".page-wrapper");
    if (pw && pw.scrollHeight > pw.clientHeight) return pw;
    return document.scrollingElement || document.documentElement;
  }
  function scrollTop() {
    return Math.max(scroller().scrollTop || 0, window.scrollY || 0);
  }

  function currentMetro() {
    var d = data();
    var fallback = (d && d.defaultMetro) || "denver";
    if (window.GPMetro) {
      var m = window.GPMetro.get();
      return d && d.markets[m] ? m : fallback;
    }
    return fallback;
  }

  /* Resolve a 5-digit ZIP against the live service-area selection.
     Returns the metro key or null. */
  function resolveZip(zip) {
    var d = data();
    if (!d || !d.zips) return null;
    // metafield may be {denver:[...],...} or {selection:{denver:[...]},...}
    var sel = d.zips.selection && typeof d.zips.selection === "object" ? d.zips.selection : d.zips;
    for (var mk in sel) {
      if (Array.isArray(sel[mk]) && sel[mk].indexOf(zip) !== -1) return mk;
    }
    return null;
  }

  /* ---------- metro links (footer serve column) ---------- */
  function bindMetroLinks(scope) {
    (scope || document).querySelectorAll("[data-gp-metro-link]").forEach(function (a) {
      if (a.getAttribute("data-gp-metro-bound")) return;
      a.setAttribute("data-gp-metro-bound", "1");
      a.addEventListener("click", function () {
        var mk = a.getAttribute("data-gp-metro-link");
        if (window.GPMetro && mk) {
          try { window.GPMetro.set(mk); } catch (e) {}
        }
        // navigation proceeds to /?metro=<mk> — param is the fallback path
      });
    });
  }

  /* Honor /?metro=omaha_lincoln etc. — set the cookie metro on landing. */
  function applyMetroParam() {
    try {
      var mk = new URLSearchParams(location.search).get("metro");
      var d = data();
      if (mk && d && d.markets[mk] && window.GPMetro && window.GPMetro.get() !== mk) {
        window.GPMetro.set(mk);
      }
    } catch (e) {}
  }

  /* ---------- market swapping ---------- */
  function applyMarket() {
    var d = data();
    if (!d) return;
    var mk = currentMetro();
    var m = d.markets[mk];
    if (!m) return;

    document.querySelectorAll("[data-gp-market]").forEach(function (el) {
      var key = el.getAttribute("data-gp-market");
      if (m[key] != null) el.textContent = m[key];
    });
    document.querySelectorAll("[data-gp-market-bg]").forEach(function (el) {
      var key = el.getAttribute("data-gp-market-bg");
      if (m[key]) el.style.backgroundImage = "url(" + m[key] + ")";
    });
    /* NOTE: [data-gp-metro-only] is deliberately NOT handled here. gp-pages.js already
       owns it, and that file is not in this repo — it exists only on the theme, so a
       grep of assets/ makes the mechanism look missing when it isn't. Two handlers on
       one attribute would fight. See the ornamental-grass row in gp-addons.liquid.

       [data-gp-metro-not] is the inverse and IS ours — a DIFFERENT attribute, so the
       two handlers never touch the same element. Added Aug-13 to strip Weather Guard
       from the Phoenix pages entirely: it is not sold there, and naming a product only
       to say you can't have it is worse than silence. Comma-separated list of metros
       to hide from. Markup stays visible by default — three of four metros should not
       pay a flash of missing content for the fourth. */
    document.querySelectorAll("[data-gp-metro-not]").forEach(function (el) {
      var hide = (el.getAttribute("data-gp-metro-not") || "").split(",").map(function (s) { return s.trim(); });
      el.hidden = hide.indexOf(mk) !== -1;
    });
    /* Oct-12 BYO freeze: markets carry "closed" (Omaha–Lincoln + Phoenix, Fall 2026).
       Closed-only elements show the closed line; closed-not elements are the booking
       paths (capture, CTAs, cart chip) that a frozen metro must not see. */
    document.querySelectorAll("[data-gp-closed-only]").forEach(function (el) { el.hidden = !m.closed; });
    document.querySelectorAll("[data-gp-closed-not]").forEach(function (el) { el.hidden = !!m.closed; });
    var serveWrap = document.querySelector("[data-gp-serve]");
    if (serveWrap && m.serve) {
      /* Aug-11 per Jeff: plain text only — cross-metro footer links let a customer
         wander into another city with no way back to their own. Names stay. */
      var html = "";
      m.serve.forEach(function (item) {
        html += '<span class="gp-foot-city">' + item[0] + "</span>";
      });
      serveWrap.innerHTML = html;
    }
  }

  /* ---------- announce/nav ---------- */
  function initNav() {
    var nav = document.querySelector("[data-gp-nav]");
    if (!nav) return;
    var onScroll = function () {
      var shrunk = scrollTop() > 40;
      nav.style.setProperty("--gp-logo", shrunk ? "51px" : "64px");
    };
    // capture-phase catches scrolls on inner containers (Horizon's .page-wrapper)
    document.addEventListener("scroll", onScroll, { passive: true, capture: true });
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();

    var btn = nav.querySelector(".gp-menu-btn");
    var menu = nav.querySelector(".gp-mobile-menu");
    if (btn && menu) {
      btn.addEventListener("click", function () {
        var open = menu.classList.toggle("is-open");
        btn.setAttribute("aria-expanded", open ? "true" : "false");
        btn.setAttribute("aria-label", open ? "Close menu" : "Open menu");
      });
    }
  }

  /* Smooth-scroll to the band + focus its input (announce/nav/footer CTAs).
     Pre-open behavior — kept for any legacy [data-gp-first-access] links. */
  function bindFirstAccess(scope) {
    (scope || document).querySelectorAll("[data-gp-first-access]").forEach(function (a) {
      if (a.getAttribute("data-gp-bound")) return;
      a.setAttribute("data-gp-bound", "1");
      a.addEventListener("click", function (e) {
        var sec = document.getElementById("notify");
        if (!sec) return; // non-homepage: follow href to /#notify
        e.preventDefault();
        var sc = scroller();
        var top = sec.getBoundingClientRect().top + scrollTop() - 84;
        sc.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
        var input = sec.querySelector("input");
        if (input) setTimeout(function () { input.focus({ preventScroll: true }); }, 550);
      });
    });
  }

  /* ---------- waitlist POST ---------- */
  function postWaitlist(email, zip) {
    var d = data();
    var backend = d && d.backend ? d.backend.replace(/\/$/, "") : "";
    if (!backend) return Promise.resolve({ ok: false });
    return fetch(backend + "/waitlist", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email: email, zip: zip || "" }),
    }).then(function (r) {
      return r.json().catch(function () { return { ok: r.ok }; });
    }).catch(function () {
      /* Network/CORS failure. Without this the promise rejects, no caller ever
         re-enables its button, and the form sits on "Adding you…" forever. */
      return { ok: false };
    });
  }

  var lastZip = ""; // remembered from the hero funnel; reused by the band form

  /* ---------- hero capture funnel (pre-open two-step; no-ops on open theme) ---------- */
  function initHeroCapture() {
    var root = document.querySelector("[data-gp-capture]");
    if (!root) return;
    var stepZip = root.querySelector("[data-gp-step-zip]");
    var stepEmail = root.querySelector("[data-gp-step-email]");
    var stepDone = root.querySelector("[data-gp-step-done]");
    var zipMsg = root.querySelector("[data-gp-zip-msg]");

    var zipForm = stepZip && stepZip.querySelector("form");
    if (zipForm) {
      zipForm.addEventListener("submit", function (e) {
        e.preventDefault();
        var v = (zipForm.querySelector("input").value || "").trim();
        if (!/^\d{5}$/.test(v)) return;
        lastZip = v;
        var mk = resolveZip(v);
        if (mk) {
          if (window.GPMetro && window.GPMetro.get() !== mk) window.GPMetro.set(mk);
          if (zipMsg) zipMsg.textContent =
            "You’re in our service area.\nLeave your email for first access when booking opens.";
        } else {
          if (zipMsg) zipMsg.textContent =
            "We’re not on your block yet.\nLeave your email and we’ll tell you when we reach you.";
        }
        stepZip.setAttribute("hidden", "hidden");
        if (stepEmail) {
          stepEmail.removeAttribute("hidden");
          var inp = stepEmail.querySelector("input");
          if (inp) inp.focus({ preventScroll: true });
        }
      });
    }

    var emailForm = stepEmail && stepEmail.querySelector("form");
    if (emailForm) {
      emailForm.addEventListener("submit", function (e) {
        e.preventDefault();
        var email = (emailForm.querySelector("input").value || "").trim();
        if (!email) return;
        var btn = emailForm.querySelector("button");
        if (btn) { btn.disabled = true; btn.textContent = "Adding you…"; }
        postWaitlist(email, lastZip).then(function (res) {
          if (res && res.ok) {
            stepEmail.setAttribute("hidden", "hidden");
            if (stepDone) stepDone.removeAttribute("hidden");
          } else {
            if (btn) { btn.disabled = false; btn.textContent = "Get first access"; }
            if (zipMsg) zipMsg.textContent = "That didn’t work — check your email address and try again.";
          }
        });
      });
    }
  }

  /* ---------- hero capture, BOOKING-OPEN flow (v11) ----------
     ZIP check → in-area: "booking is open" + Book your porch CTA (metro set so the
     booking page opens on the right market); out: "not on your block yet".
     v20 (2026-08-12): out-of-area no longer dead-ends. The ZIP we just rejected is
     the most useful thing we know about that visitor, so the miss now offers the
     waitlist — same /waitlist POST the pre-open funnel used. The backend segments
     by ZIP, so these land under "unknown" and can be mailed when a market opens. */
  function initHeroCaptureOpen() {
    var root = document.querySelector("[data-gp-capture-open]");
    if (!root) return;
    var stepZip = root.querySelector("[data-gp-step-zip]");
    var stepResult = root.querySelector("[data-gp-step-result]");
    var zipMsg = root.querySelector("[data-gp-zip-msg]");
    var bookCta = root.querySelector("[data-gp-book-cta]");
    var wlForm = root.querySelector("[data-gp-waitlist-form]");
    var wlDone = root.querySelector("[data-gp-waitlist-done]");
    var altLink = root.querySelector("[data-gp-capture-alt]");
    var zipForm = stepZip && stepZip.querySelector("form");
    if (!zipForm) return;
    zipForm.addEventListener("submit", function (e) {
      e.preventDefault();
      var v = (zipForm.querySelector("input").value || "").trim();
      if (!/^\d{5}$/.test(v)) return;
      lastZip = v;
      var mk = resolveZip(v);
      if (mk) { try { localStorage.setItem("gp-zip", v); } catch (err) {} } /* flow inherits it */
      /* Oct-12 BYO freeze: a ZIP inside a FROZEN metro is in-area but not bookable —
         say closed and offer the waitlist, never "booking is open". The metro cookie
         is deliberately NOT switched for a closed metro: applyMarket would hide this
         whole capture block (data-gp-closed-not) mid-interaction and take the
         waitlist offer with it. */
      var d2 = data();
      var closed = !!(mk && d2 && d2.markets[mk] && d2.markets[mk].closed);
      if (mk && !closed && window.GPMetro && window.GPMetro.get() !== mk) {
        try { window.GPMetro.set(mk); } catch (err) {}
      }
      if (zipMsg) zipMsg.textContent = closed
        ? (d2.markets[mk].closedLine || "Fall 2026 booking is closed in your area.") + "\nLeave your email and we’ll tell you when booking reopens."
        : mk
          ? "You’re in our service area — booking is open."
          : "We’re not on your block yet this season.\nLeave your email and we’ll tell you when we reach you.";
      if (bookCta) bookCta.hidden = !mk || closed;
      if (closed) mk = null; /* fall through to the waitlist branch below */
      /* In-area: the primary CTA above points at /pages/packages too, so the alt
         link is a duplicate — drop it (Jeff, Aug-13). Out-of-area keeps it: the
         waitlist form replaces the primary there, leaving no other way onward. */
      if (altLink) altLink.hidden = !!mk;
      if (wlForm) {
        wlForm.hidden = !!mk;
        if (!mk) {
          var inp = wlForm.querySelector("input");
          if (inp) inp.focus({ preventScroll: true });
        }
      }
      stepZip.setAttribute("hidden", "hidden");
      if (stepResult) stepResult.removeAttribute("hidden");
    });

    if (wlForm) {
      wlForm.addEventListener("submit", function (e) {
        e.preventDefault();
        var input = wlForm.querySelector("input");
        var email = (input && input.value || "").trim();
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
          if (zipMsg) zipMsg.textContent = "That email doesn’t look right — check it and try again.";
          if (input) input.focus({ preventScroll: true });
          return;
        }
        var btn = wlForm.querySelector("button");
        var btnHtml = btn ? btn.innerHTML : ""; /* keep the arrow span for the restore */
        if (btn) { btn.disabled = true; btn.textContent = "Adding you…"; }
        postWaitlist(email, lastZip).then(function (res) {
          if (res && res.ok) {
            wlForm.hidden = true;
            if (zipMsg) zipMsg.hidden = true;
            if (wlDone) wlDone.hidden = false;
          } else {
            if (btn) { btn.disabled = false; btn.innerHTML = btnHtml; }
            /* Only blame the address when the backend actually rejected it. A
               network/CORS failure is our problem, and telling someone to check a
               perfectly good email just sends them hunting for a typo. */
            if (zipMsg) zipMsg.textContent = res && res.error === "invalid_email"
              ? "That email doesn’t look right — check it and try again."
              : "We couldn’t reach our sign-up just now. Please try again in a moment.";
          }
        });
      });
    }
  }

  /* ---------- band signup (pre-open; no-ops without a [data-gp-band] form) ---------- */
  function initBandSignup() {
    var band = document.querySelector("[data-gp-band]");
    if (!band) return;
    var form = band.querySelector("form");
    var done = band.querySelector("[data-gp-band-done]");
    if (!form) return;
    form.addEventListener("submit", function (e) {
      e.preventDefault();
      var email = (form.querySelector("input").value || "").trim();
      if (!email) return;
      var btn = form.querySelector("button");
      if (btn) { btn.disabled = true; btn.textContent = "Adding you…"; }
      postWaitlist(email, lastZip).then(function (res) {
        if (res && res.ok) {
          form.setAttribute("hidden", "hidden");
          if (done) done.removeAttribute("hidden");
        } else {
          if (btn) { btn.disabled = false; btn.textContent = "Notify me"; }
        }
      });
    });
  }

  /* ---------- scroll memory (v12) ----------
     Save the scroller position per URL (hash ignored) while the visitor scrolls;
     restore it when they arrive via back/forward. bfcache restores (pageshow with
     persisted=true) already keep the position, so those are left alone. Fresh
     navigations still start at the top (or at the #hash the browser handles). */
  function initScrollMemory() {
    var KEY = "gp-scroll:" + location.pathname + location.search;
    var queued = false;
    function save() {
      queued = false;
      try { sessionStorage.setItem(KEY, String(Math.round(scrollTop()))); } catch (e) {}
    }
    document.addEventListener("scroll", function () {
      if (!queued) { queued = true; requestAnimationFrame(save); }
    }, { passive: true, capture: true });
    window.addEventListener("pagehide", save);

    function jump(sc, top) {
      try { sc.scrollTo({ top: top, behavior: "instant" }); } catch (e) { sc.scrollTop = top; }
    }
    function restore() {
      var nav = null;
      try { nav = performance.getEntriesByType("navigation")[0]; } catch (e) {}
      if (!nav || nav.type !== "back_forward") return;
      var v = null;
      try { v = sessionStorage.getItem(KEY); } catch (e) {}
      var top = parseInt(v, 10);
      if (!top) return;
      var sc = scroller();
      jump(sc, top);
      // late-loading images reflow the page — reassert once everything has sized
      window.addEventListener("load", function () {
        var cur = Math.max(sc.scrollTop || 0, window.scrollY || 0);
        if (Math.abs(cur - top) > 40) jump(scroller(), top);
      }, { once: true });
    }
    window.addEventListener("pageshow", function (ev) { if (!ev.persisted) restore(); });
    restore();
  }

  /* ---------- cart icon (v16, Aug-7 cart restructure) ----------
     A standard cart signal: bag icon + total → /cart, shown while the cart
     (draft v2) holds packages. The Book CTA stays visible — no more text swaps.
     Same 7-day window the booking page honors; re-checked on every pageshow. */
  var chipRun = 0;
  /* Oct-12 BYO freeze: the chip links the builder now, so a frozen metro must not
     see it — "always visible" (Aug-11) holds only where booking is open. */
  function chipAllowed() {
    var d = data();
    var m = d && d.markets[currentMetro()];
    return !(m && m.closed);
  }
  /* Authoritative path: /cart hands us the figure its own panel is showing. */
  function setChipTotal(d) {
    var chip = document.querySelector("[data-gp-cart-chip]");
    var chipLabel = document.querySelector("[data-gp-cart-chip-label]");
    if (!chip) return;
    chipRun++; /* cancel any /cart.js reply still in flight */
    chip.hidden = !chipAllowed();
    if (chipLabel) {
      chipLabel.textContent = d.count
        ? "$" + (d.total || 0).toLocaleString("en-US", { maximumFractionDigits: 2 })
        : "";
    }
  }
  function initResumeCta() {
    var draft = null;
    try { draft = JSON.parse(localStorage.getItem("gp-booking-draft")); } catch (e) {}
    var live = draft && draft.v === 2 && draft.summary && draft.summary.count &&
      Date.now() - (draft.at || 0) <= 7 * 864e5;
    var chip = document.querySelector("[data-gp-cart-chip]");
    var chipLabel = document.querySelector("[data-gp-cart-chip-label]");
    if (!chip) return;
    /* Every run takes a ticket; only the newest may write the label. Without this
       a slow /cart.js reply could land after a newer gp:cart and repaint an old
       total over a correct one. */
    var run = ++chipRun;
    /* Aug-11 per Jeff: the chip is always shown — bare icon when empty, icon +
       total when the cart holds a build. (The nav CTA button was removed.)
       Except in a frozen metro — see chipAllowed(). */
    chip.hidden = !chipAllowed();
    if (!live) {
      if (chipLabel) chipLabel.textContent = "";
      /* v17: the draft clears at checkout handoff — if the customer backs out of
         checkout and lands on a marketing page, the REAL Shopify cart still has
         their order. Fall back to it so the cart signal survives.
         v19 (Aug-12, Jeff): NOT on the cart page. There, gp-booking.js rebuilds the
         draft from the Shopify cart on load and rewrites it on every change, so the
         draft IS the truth — and a second opinion from /cart.js could only disagree
         with what the page itself is showing ("Your cart is empty" under a $1,580
         bag). Removing the last package must leave the bag empty, full stop. */
      if (document.querySelector("[data-gpc-root]")) return;
      fetch("/cart.js")
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (c) {
          if (run !== chipRun) return; /* a newer run already spoke */
          if (!c || !c.item_count) return;
          if (chipLabel) chipLabel.textContent = "$" + (c.total_price / 100).toLocaleString("en-US", { maximumFractionDigits: 2 });
        })
        .catch(function () {});
      return;
    }
    /* Aug-12 (Jeff): the chip shows the SAME pre-tax number as the cart's
       "Total at checkout" — exact, never rounded, tax never folded in. */
    var total = draft.summary.total || 0;
    if (chipLabel) chipLabel.textContent = "$" + total.toLocaleString("en-US", { maximumFractionDigits: 2 });
  }

  function boot() {
    initScrollMemory();
    initResumeCta();
    window.addEventListener("pageshow", initResumeCta);
    /* Aug-12 (Jeff): the cart page changes the total in place (ZIP zone fee, qty,
       remove) without a reload — gp-booking.js fires gp:cart when the total
       actually moves, so the bag never shows a stale number. When the event
       carries a detail, /cart is telling us the exact figure its panel shows
       (the draft counts packages only, so a gift-card-only cart would read as
       empty) — that always wins over re-deriving it. */
    document.addEventListener("gp:cart", function (ev) {
      if (ev && ev.detail && typeof ev.detail.total === "number") { setChipTotal(ev.detail); return; }
      initResumeCta();
    });
    initNav();
    bindFirstAccess(document);
    bindMetroLinks(document);
    initHeroCapture();
    initHeroCaptureOpen();
    initBandSignup();
    if (window.GPMetro) applyMetroParam();
    applyMarket();
    document.addEventListener("gp:metro", applyMarket);
    // GPMetro is loaded by the app embed near </body> and may land after us —
    // re-apply once it shows up so the tz/cookie metro takes over from the default.
    if (!window.GPMetro) {
      var tries = 0;
      var t = setInterval(function () {
        tries++;
        if (window.GPMetro || tries > 24) {
          clearInterval(t);
          if (window.GPMetro) { applyMetroParam(); applyMarket(); }
        }
      }, 250);
    }
    window.addEventListener("load", applyMarket);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})();
